import React, { useState, useEffect } from 'react';
import { verifyPastoralAccess } from '../../api/config';
import { Banner, Button, Card, EmptyState, PageBody, PageHeader, cx } from '../ui';
import BulletinManager from './BulletinManager';
import GivingDashboard from './GivingDashboard';
import PrayerRequestsManager from './PrayerRequestsManager';
import SermonNotesManager from './SermonNotesManager';

const TABS = [
    { key: 'bulletins', label: 'Weekly bulletins' },
    { key: 'sermons', label: 'Sermon notes' },
    { key: 'giving', label: 'Weekly giving' },
    { key: 'prayers', label: 'Prayer requests' },
];

const Tabs = ({ active, onChange }) => (
    <div className="-mb-px flex gap-1 overflow-x-auto">
        {TABS.map((tab) => (
            <button
                key={tab.key}
                type="button"
                onClick={() => onChange(tab.key)}
                className={cx(
                    'whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                    active === tab.key
                        ? 'border-brand-700 text-brand-800'
                        : 'border-transparent text-neutral-500 hover:border-neutral-300 hover:text-neutral-800'
                )}
            >
                {tab.label}
            </button>
        ))}
    </div>
);

const PastorsTools = () => {
    const [activeTab, setActiveTab] = useState('bulletins');
    const [hasAccess, setHasAccess] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [bethelUser, setBethelUser] = useState(null);
    const [formsUser, setFormsUser] = useState(null);

    useEffect(() => {
        // Pastor's Tools no longer repoints the global Parse SDK at the app
        // server — the auth check and reads go over direct fetch, and writes go
        // through the forms server. So there's nothing to hand back on the way
        // out, and no way for this page to disturb the rest of the app's session.
        checkPastoralAccess();
    }, []);

    const checkPastoralAccess = async () => {
        try {
            setLoading(true);
            setError('');

            const storedUser = localStorage.getItem('user');
            if (!storedUser) {
                setError('Please sign in first.');
                setLoading(false);
                return;
            }

            const formsUserData = JSON.parse(storedUser);
            setFormsUser(formsUserData);

            if (!formsUserData.email) {
                setError('No email is on your profile. Contact an administrator.');
                setLoading(false);
                return;
            }

            const verification = await verifyPastoralAccess(formsUserData.email);

            if (verification.error) {
                setError(`We couldn't verify your access: ${verification.error}`);
                setLoading(false);
                return;
            }

            if (verification.hasAccess) {
                setHasAccess(true);
                setBethelUser(verification.user);
            } else {
                setError(`Your account (${formsUserData.email}) isn't on the pastoral team.`);
            }
        } catch (err) {
            console.error('Pastoral access check failed:', err);
            setError('We could not verify your access. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <>
                <PageHeader title="Pastor's Tools" description="Manage the member app from the forms system." />
                <PageBody>
                    <Card>
                        <EmptyState title="Verifying your access…" />
                    </Card>
                </PageBody>
            </>
        );
    }

    if (!hasAccess) {
        return (
            <>
                <PageHeader title="Pastor's Tools" description="Manage the member app from the forms system." />
                <PageBody className="space-y-4">
                    <Banner tone="danger" title="You don't have access to Pastor's Tools">
                        {error || 'This area is limited to the pastoral team.'}
                        {formsUser?.email && (
                            <>
                                {' '}
                                You're signed in as {formsUser.name ? `${formsUser.name} (${formsUser.email})` : formsUser.email}.
                                If that's wrong, contact an administrator.
                            </>
                        )}
                    </Banner>
                    <Button variant="secondary" onClick={checkPastoralAccess}>
                        Try again
                    </Button>
                </PageBody>
            </>
        );
    }

    return (
        <>
            <PageHeader
                title="Pastor's Tools"
                description={
                    bethelUser?.firstName
                        ? `Welcome, ${bethelUser.firstName}. Manage the member app from here.`
                        : 'Manage the member app — bulletins, giving, and prayer requests.'
                }
            >
                <div className="mx-auto max-w-6xl px-4 pb-3 sm:px-6 lg:px-8">
                    <Tabs active={activeTab} onChange={setActiveTab} />
                </div>
            </PageHeader>

            <PageBody className="space-y-4">
                {activeTab === 'bulletins' && <BulletinManager />}
                {activeTab === 'sermons' && <SermonNotesManager />}
                {activeTab === 'giving' && <GivingDashboard />}
                {activeTab === 'prayers' && <PrayerRequestsManager />}
            </PageBody>
        </>
    );
};

export default PastorsTools;
